import { useEffect, useState, useCallback } from 'react';
import { ItemToReturn } from '@src/models/returns';
import { fetchRefundEstimationAPI } from '@src/utils/api-helpers';
import { buildRefundEstimationRequest } from '@src/utils';
import { setRefundValues } from '@src/stores/summaryStore';
import { useReturnStore } from '@src/stores/returnStore';

interface UsePartialReturnRefundParams {
  successfulItems: ItemToReturn[];
  totalSelectedItems: number;
  onFinishReturning: () => Promise<void>;
}

export const usePartialReturnRefund = ({
  successfulItems,
  totalSelectedItems,
  onFinishReturning,
}: UsePartialReturnRefundParams) => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const [isCalculatingRefund, setIsCalculatingRefund] = useState(false);
  const [showButtonLoader, setShowButtonLoader] = useState(false);

  // Proactively calculate refund for successful items when partial failure occurs
  useEffect(() => {
    const isPartialFailure =
      successfulItems.length > 0 && successfulItems.length < totalSelectedItems;

    if (isPartialFailure && orderDetailsV2) {
      setIsCalculatingRefund(true);
      const refundRequest = buildRefundEstimationRequest(
        orderDetailsV2,
        successfulItems,
      );

      fetchRefundEstimationAPI(refundRequest)
        .then((refundAmount) => {
          setRefundValues(refundAmount);
        })
        .catch(() => {
          setRefundValues(undefined);
        })
        .finally(() => {
          setIsCalculatingRefund(false);
        });
    }
  }, [successfulItems, totalSelectedItems, orderDetailsV2]);

  const handleContinueClick = useCallback(async () => {
    // If refund is still calculating, show loader and wait
    if (isCalculatingRefund) {
      setShowButtonLoader(true);
      return;
    }

    // Refund already calculated, proceed immediately
    await onFinishReturning();
  }, [isCalculatingRefund]);

  // Navigate when refund calculation completes if user already clicked the button
  useEffect(() => {
    if (showButtonLoader && !isCalculatingRefund) {
      onFinishReturning();
    }
  }, [isCalculatingRefund, showButtonLoader]);

  return {
    isCalculatingRefund,
    showButtonLoader,
    handleContinueClick,
  };
};
